import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import { Breadcrumbs, Typography, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";

const StyledLink = styled(Link)({
  textDecoration: "none",
});

export const AssetBreadcrumbs = ({
  breadcrumbData,
  rootId,
  version = "default",
}) => {
  const theme = useTheme();

  const breadcrumbs = breadcrumbData.map((item, index) => {
    const breadcrumbContent = (
      <Typography
        variant="body2"
        sx={{
          fontSize: "0.8rem",
          color: theme.palette.text.primary,
          textDecoration: "none",
          "&:hover": {
            color: theme.palette.brandText,
          },
          ...(version === "mini" && {
            color: theme.palette.text.secondary,
            fontSize: "0.65rem",
            "&:hover": {
              color: theme.palette.text.secondary,
            },
          }),
        }}
        data-testid="breadcrumb"
      >
        {item.value}
      </Typography>
    );

    return version === "mini" ? (
      <span key={item.key}>{breadcrumbContent}</span>
    ) : (
      <StyledLink
        to={
          index === 0 ? `/assets/${rootId}` : `/assets/${rootId}/${item.key}`
        }
        key={item.key}
      >
        {breadcrumbContent}
      </StyledLink>
    );
  });

  if (version === "mini") {
    return (
      <Breadcrumbs separator="›" sx={{ fontSize: "0.65rem" }}>
        {breadcrumbs.slice(1, -1)}
      </Breadcrumbs>
    );
  }

  // Default view hides the last crumb because the page title already shows it.
  // On screen the trail collapses to two items; collapsed crumbs aren't in the
  // DOM, so print gets its own uncollapsed copy. It renders as a div because
  // the global print styles hide every <nav> (Breadcrumbs' default element).
  return (
    <>
      <Breadcrumbs
        className="no-print"
        maxItems={2}
        separator="›"
        sx={{ opacity: 0.75, fontSize: "0.8rem" }}
      >
        {breadcrumbs.slice(0, -1)}
      </Breadcrumbs>
      <Breadcrumbs
        component="div"
        aria-hidden="true"
        separator="›"
        sx={{
          display: "none",
          opacity: 0.75,
          fontSize: "0.8rem",
          "@media print": { display: "block" },
        }}
      >
        {breadcrumbs.slice(0, -1)}
      </Breadcrumbs>
    </>
  );
};

AssetBreadcrumbs.propTypes = {
  breadcrumbData: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  rootId: PropTypes.string.isRequired,
  version: PropTypes.oneOf(["default", "mini"]),
};
